import { ApiSaleOrderService, ApiService } from '../../services';
import { AxiosResponse } from 'axios';
import {
	IBulkPaymentItem,
	IBulkPaymentResponseItem,
	ICalendarDayData,
	IChannel,
	ICodeName,
	ICostVariationLogs,
	ICustomersAutocomplete,
	IFlightServiceLeg,
	IInvoiceInfo,
	IOperationalRequest,
	IOperationalRequestListItem,
	IOperationalRequestResponse,
	IPassenger,
	IPassengerAutocomplete,
	IRelatedPax,
	IPatchSaleOrder,
	IProviderAutocomplete,
	IProviderPayableLineListFilters,
	IProviderPayableLineListItem,
	IProviderPayableLineListResponse,
	IProviderPayableLineScheduleFilters,
	IProviderPayableLineScheduleResponse,
	IProviderPayableLinePayment,
	IProviderPayableLinePaymentCreateDTO,
	IProviderPayableLinePaymentListFilters,
	IProviderPayableLinePaymentListResponse,
	IProviderPayableLinePaymentUpdateDTO,
	IProviderPayableLinePending,
	IReceipt,
	IReceiptFilters,
	IReceiptListResponse,
	ISaleOrderCreateForm,
	ISaleOrderDetail,
	ISaleOrderIdResponse,
	ISaleOrderListItem,
	ISaleOrderPendingPayMessageScheduleCreate,
	ISaleOrderPendingPayMessageScheduleList,
	ISalesOrderCustomer,
	ISalesOrderList,
	ISalesOrderServicesList,
	ISelectOption,
	IServiceCostVariationLogs,
	ITransaction,
	IUserList,
	IUserSummary,
	NetsuitePlaces,
	NetsuiteTaxDataResponse,
	SegmentEnum,
} from './types';
import { ISort } from 'features/common/types';
import { formatSortingFields } from 'features/common/helpers';
import { ICustomerLeisureDTO } from 'features/customers/types';
import {
	AccommodationServiceProviderTypeEnum,
	AccomodationProviderItem,
} from 'features/common/components/AutocompleteAccomodationProviders';
import { FlightLeg } from 'pages/SalesOrder/SalesOrderScreen/components/ServiceForm/FlightService';

interface FlightLegRequest extends FlightLeg {
	keyId: string;
}

export const createSalesOrder = (
	id: string,
	orderToSend: ISaleOrderCreateForm,
): Promise<AxiosResponse<ISaleOrderCreateForm>> => {
	return ApiSaleOrderService.post(`/saleorders/${id}`, orderToSend);
};

export const getSalesOrderList = (
	page = 0,
	size = 10,
	sortingFields: ISort[],
	filters?: Record<string, string | number | boolean>,
	search?: string,
): Promise<AxiosResponse<ISalesOrderList>> => {
	const sorts = formatSortingFields(sortingFields);
	return ApiSaleOrderService.get('/saleorders', {
		params: {
			page,
			size,
			...(sorts.length > 0 && { sort: sorts }),
			...(filters && filters),
			...(search && { search: search }),
			envelope: true,
		},
	});
};

export const getPaymentReceivedTransactions = (id: string): Promise<AxiosResponse<ITransaction[]>> => {
	return ApiSaleOrderService.get(`/saleorders/${id}/payment-received`);
};

export const getSalesOrderListToExport = (
	sortingFields: ISort[],
	filters?: Record<string, string | number | boolean>,
): Promise<AxiosResponse<ISaleOrderListItem[]>> => {
	const sorts = formatSortingFields(sortingFields);
	return ApiSaleOrderService.get('/saleorders/export-all', {
		params: {
			...(sorts.length > 0 && { sort: sorts }),
			...(filters && filters),
		},
	});
};

export const getSalesOrderId = (
	id: string,
	options?: { agencyId?: number },
): Promise<AxiosResponse<ISaleOrderDetail>> =>
	ApiSaleOrderService.get(`/saleorders/${id}`, { agencyId: options?.agencyId });

/** Una orden de venta por caso (dealId / crmTicket). */
export const getSaleOrderByDealId = (dealId: number): Promise<AxiosResponse<ISalesOrderList>> =>
	ApiSaleOrderService.get('/saleorders', {
		params: {
			page: 1,
			size: 1,
			sort: '-created',
			dealId,
			envelope: true,
		},
	});

export const getInvoiceablePassengersBySalesOrderId = (id: string): Promise<AxiosResponse<IPassenger[]>> =>
	ApiSaleOrderService.get(`/saleorders/${id}/invoiceable-passengers`);

export const patchPassengerForm = (id: string, passengerData: IPassenger[]): Promise<AxiosResponse> =>
	ApiSaleOrderService.patch(`/saleorders/${id}/passengers/`, passengerData);

export const patchServiceForm = (id: string, data: any[]): Promise<AxiosResponse> =>
	ApiSaleOrderService.patch(`/saleorders/${id}/services`, data);

export const postSendAccomodationServiceVoucher = (
	saleOrderId: string,
	serviceId: string,
	sendByWhatsApp: boolean,
): Promise<AxiosResponse> =>
	ApiSaleOrderService.post(`/saleorders/${saleOrderId}/accommodation-services/${serviceId}/vouchers`, {
		sendByWhatsapp: sendByWhatsApp,
	});

//@ts-ignore
export const deleteFlightLeg = (saleOrderId: string, serviceId: string, legId: string): Promise<AxiosResponse> => {
	return ApiSaleOrderService.delete(`/saleorders/${saleOrderId}/flight-services/${serviceId}/legs/${legId}`);
};
//@ts-ignore
export const putFlightLeg = (
	saleOrderId: string,
	serviceId: string,
	data: FlightLegRequest,
): Promise<AxiosResponse> => {
	return ApiSaleOrderService.put(`/saleorders/${saleOrderId}/flight-services/${serviceId}/legs`, data);
};

export const patchSaleOrder = (saleOrderId: string, data: IPatchSaleOrder): Promise<AxiosResponse> =>
	ApiSaleOrderService.patch(`/saleorders/${saleOrderId}`, data);

export const addToQueueToSyncWithNetsuite = (saleOrderId: string): Promise<AxiosResponse> =>
	ApiSaleOrderService.put(`/saleorders/sync/${saleOrderId}`, {});

export const matchSaleRepresentatives = (): Promise<AxiosResponse> =>
	ApiSaleOrderService.put('/sale-representative', {});

export const patchEnableService = ({
	saleOrderId,
	serviceId,
	enable,
	agencyId,
}: {
	saleOrderId: string;
	serviceId: string;
	enable: boolean;
	agencyId?: number;
}): Promise<AxiosResponse> =>
	ApiSaleOrderService.patch(`/saleorders/${saleOrderId}/services/${serviceId}/enable`, { enable }, { agencyId });

export const putServiceForm = ({
	saleOrderId,
	serviceId,
	data,
	agencyId,
}: {
	saleOrderId: string;
	serviceId: string;
	data: any;
	agencyId?: number;
}): Promise<AxiosResponse> =>
	ApiSaleOrderService.put(`/saleorders/${saleOrderId}/services/${serviceId}`, data, { agencyId });

export const getProviderAutocomplete = (
	value: string,
	agencyId?: number,
): Promise<AxiosResponse<IProviderAutocomplete[]>> =>
	ApiSaleOrderService.get('/providers/autocomplete', {
		params: { value },
		agencyId,
	});

export const getAccomodationProviderAutocomplete = (
	search: string,
): Promise<AxiosResponse<AccomodationProviderItem[]>> => {
	return ApiSaleOrderService.get('/accommodation-service-providers/autocomplete', {
		params: {
			search,
			limit: 200,
		},
	});
};
///accommodation-service-providers/{accommodationServiceProviderId}
export const getAccomodationProviderId = (
	providerId: string,
	accommodationServiceProviderType: AccommodationServiceProviderTypeEnum,
): Promise<AxiosResponse<IProviderAutocomplete>> =>
	ApiSaleOrderService.get(`/accommodation-service-providers/${providerId}/${accommodationServiceProviderType}`);

export const getProviderId = (providerId: string, agencyId?: number): Promise<AxiosResponse<IProviderAutocomplete>> =>
	ApiSaleOrderService.get(`/providers/${providerId}`, { agencyId });

export const getCurrencies = (): Promise<AxiosResponse<ICodeName[]>> => ApiSaleOrderService.get('/currencies');

export const getOIDIssues = (): Promise<AxiosResponse<ISelectOption[]>> => ApiSaleOrderService.get('/oidissues');

export const getAutocompletePassenger = (value: string): Promise<AxiosResponse<IPassengerAutocomplete[]>> => {
	return ApiSaleOrderService.get('/passengers/autocomplete', {
		params: {
			value,
		},
	});
};

export const getPassengerId = (id: string): Promise<AxiosResponse<IPassenger>> =>
	ApiSaleOrderService.get(`/passengers/${id}`);

export const getRelatedPaxes = (passengerId: string): Promise<AxiosResponse<IRelatedPax[]>> =>
	ApiSaleOrderService.get(`/passengers/related-paxes/${passengerId}`);

export const getSalesOrderCustomerList = (
	page = 0,
	size = 10,
	sortingFields: ISort[],
	filters?: Record<string, string | number | boolean>,
): Promise<AxiosResponse<ISalesOrderCustomer>> => {
	const sorts = formatSortingFields(sortingFields);
	return ApiSaleOrderService.get('/customers/leisures', {
		params: {
			page,
			size,
			...(sorts.length > 0 && { sort: sorts }),
			...(filters && filters),
			envelope: true,
		},
	});
};

export const getSalesOrderCustomerListToExport = (
	sortingFields: ISort[],
	filters?: Record<string, string | number | boolean>,
): Promise<AxiosResponse<ISalesOrderCustomer>> => {
	const sorts = formatSortingFields(sortingFields);
	return ApiSaleOrderService.get('/customers/leisures', {
		params: {
			page: 1,
			...(sorts.length > 0 && { sort: sorts }),
			...(filters && filters),
			envelope: true,
		},
	});
};

export const parseGDSAirRawDataSaleOrder = async (gdsRawData: string): Promise<AxiosResponse<IFlightServiceLeg[]>> =>
	ApiService.post('api/quotations/flights/converters/tst', gdsRawData, {
		headers: { 'Content-Type': 'text/plain' },
	});

export const getCostVariationLogs = (id: string): Promise<AxiosResponse<ICostVariationLogs>> =>
	ApiSaleOrderService.get(`/saleorders/${id}/costvariationlogs`);

export const getCostVariationLogsByService = (
	saleOrderId: string,
	serviceId: string,
	agencyId?: number,
): Promise<AxiosResponse<IServiceCostVariationLogs[]>> =>
	ApiSaleOrderService.get(`/saleorders/${saleOrderId}/services/${serviceId}/cost-variation-logs`, {
		agencyId,
	});

export const getCustomerAutocomplete = (
	value: string,
	customerType: SegmentEnum,
): Promise<AxiosResponse<ICustomersAutocomplete[]>> => {
	return ApiSaleOrderService.get('/customers/autocomplete', {
		params: {
			value,
			customerType,
		},
	});
};

export const getCustomerById = (id: string): Promise<AxiosResponse<ICustomerLeisureDTO>> =>
	ApiSaleOrderService.get(`/customers/${id}`);

export const asociateCustomerToSaleOrder = (id: string, customerId: string): Promise<AxiosResponse> =>
	ApiSaleOrderService.patch(`/saleorders/${id}/customers/${customerId}`);

export const getPax = ({ paxId }: { paxId: string }): Promise<AxiosResponse> =>
	ApiSaleOrderService.get(`/passengers/${paxId}`);

export const removePax = ({ saleOrderId, paxId }: { saleOrderId: string; paxId: string }): Promise<AxiosResponse> =>
	ApiSaleOrderService.delete(`/saleorders/${saleOrderId}/passengers/${paxId}`);

export const updatePax = ({
	saleOrderId,
	paxId,
	payload,
}: {
	saleOrderId: string;
	paxId: string;
	payload: IPassenger;
}): Promise<AxiosResponse> => ApiSaleOrderService.put(`/saleorders/${saleOrderId}/passengers/${paxId}`, payload);

export const netsuiteTaxData = (): Promise<AxiosResponse<NetsuiteTaxDataResponse>> =>
	ApiSaleOrderService.get('netsuite-tax-data');

export const getVariationLogs = (saleOrderId: string): Promise<AxiosResponse> =>
	ApiSaleOrderService.get(`/saleorders/${saleOrderId}/cost-variation-logs`);

export const getChannels = (): Promise<AxiosResponse<IChannel[]>> => {
	return ApiSaleOrderService.get('/channels');
};

export const getNetsuitePlaces = (): Promise<AxiosResponse<NetsuitePlaces>> =>
	ApiSaleOrderService.get('/netsuite-places');

export const getPendingPayMessageSchedule = (
	saleOrderId: string,
): Promise<AxiosResponse<ISaleOrderPendingPayMessageScheduleList[]>> =>
	ApiSaleOrderService.get(`/saleorders/${saleOrderId}/pending-pay-message-schedules`);

export const createPendingPayMessageSchedule = (
	saleOrderId: string,
	x: ISaleOrderPendingPayMessageScheduleCreate,
): Promise<AxiosResponse<void>> =>
	ApiSaleOrderService.post(`/saleorders/${saleOrderId}/pending-pay-message-schedules`, x);

export const sendFlightServiceIssueRequest = (
	saleOrderId: string,
	flightServiceId: string,
): Promise<AxiosResponse<void>> =>
	ApiSaleOrderService.put(`/saleorders/${saleOrderId}/flight-services/${flightServiceId}/issue-request`);

export const sendFlightAncillaryServiceIssueRequest = (
	saleOrderId: string,
	flightAncillaryServiceId: string,
): Promise<AxiosResponse<void>> =>
	ApiSaleOrderService.put(
		`/saleorders/${saleOrderId}/flight-ancillary-services/${flightAncillaryServiceId}/issue-request`,
	);

export const sendAccommodationServiceIssueRequest = (
	saleOrderId: string,
	accommodationServiceId: string,
): Promise<AxiosResponse<void>> =>
	ApiSaleOrderService.put(`/saleorders/${saleOrderId}/accommodation-services/${accommodationServiceId}/issue-request`);

export const sendOtherServiceIssueRequest = (
	saleOrderId: string,
	otherServiceId: string,
): Promise<AxiosResponse<void>> =>
	ApiSaleOrderService.put(`/saleorders/${saleOrderId}/other-services/${otherServiceId}/issue-request`);

export const getProviderIssueCostRules = (): Promise<AxiosResponse> =>
	ApiSaleOrderService.get('/provider-issue-cost-rules');

// Nuevos servicios para el calendario
export const getUserSummary = (): Promise<AxiosResponse<IUserSummary[]>> => ApiSaleOrderService.get('/user-summary');

export const getSalesOrderCalendar = (
	travelDepartureDateFrom: string,
	travelDepartureDateTo: string,
	createdByIds: string,
): Promise<AxiosResponse<ICalendarDayData[]>> =>
	ApiSaleOrderService.get(
		`/saleorders/travel-departure-calendar?travelDepartureDateFrom=${travelDepartureDateFrom}&travelDepartureDateTo=${travelDepartureDateTo}&createdByIds=${encodeURIComponent(
			createdByIds,
		)}`,
	);

export const getSaleOrderIdFromNetsuiteId = (netsuiteId: number): Promise<AxiosResponse<ISaleOrderIdResponse>> =>
	ApiSaleOrderService.get(`/saleorders/by-external-id/${netsuiteId}`);

export const getInvoiceInfo = (invoiceIdInNetsuite: number): Promise<AxiosResponse<IInvoiceInfo>> =>
	ApiSaleOrderService.get(`/invoices/${invoiceIdInNetsuite}`);

export const changeSellerOfSaleOrder = (saleOrderId: string, newSellerUserId: number): Promise<AxiosResponse<void>> =>
	ApiSaleOrderService.patch(`/saleorders/${saleOrderId}/seller`, { newSellerId: newSellerUserId });

export const getAllUsersActivesByAgencyId = (agencyId: number): Promise<AxiosResponse<IUserList[]>> =>
	ApiService.get(`/v1/mevuelo/backend/users/all-active-of-agency/${agencyId}`);

const buildServicesParams = (
	page: number,
	size: number,
	sortingFields: ISort[],
	filters?: Record<string, string | number | boolean>,
	search?: string,
): Record<string, string | number | boolean> => {
	const sorts = formatSortingFields(sortingFields);
	const params: Record<string, string | number | boolean> = { page, size, envelope: true };

	if (sorts.length > 0) {
		params.sort = sorts;
	}

	if (search) {
		params.search = search;
	}

	if (filters) {
		Object.entries(filters).forEach(([key, value]) => {
			if (value === undefined || value === null || value === '') return;
			params[key] = value;
		});
	}

	return params;
};

export const getServicesList = (
	page = 1,
	size = 15,
	sortingFields: ISort[],
	filters?: Record<string, string | number | boolean>,
	search?: string,
	signal?: AbortSignal,
): Promise<AxiosResponse<ISalesOrderServicesList>> =>
	ApiSaleOrderService.get('/services', {
		params: buildServicesParams(page, size, sortingFields, filters, search),
		signal,
	});

// ==================== SERVICIOS PARA SOLICITUDES OPERATIVAS ====================

export const createOperationalRequest = async (
	operationalReqeustData: IOperationalRequest,
): Promise<AxiosResponse<IOperationalRequestResponse>> => {
	return ApiSaleOrderService.post('/operational-requests', operationalReqeustData);
};

export const getOperationalRequests = (saleOrderId: string): Promise<AxiosResponse<IOperationalRequestListItem[]>> => {
	return ApiSaleOrderService.get(`/operational-requests/sale-orders/${saleOrderId}`);
};

// ==================== PAGOS A PROVEEDORES ====================

export interface IProviderPayableLinesFilters {
	saleOrderId?: string;
	providerId?: string;
	serviceId?: string;
	ticket?: string;
	paymentType?: string;
	dateFrom?: string;
	dateTo?: string;
}

export const getProviderPayableLinePayments = (
	providerPayableLineId: string,
): Promise<AxiosResponse<IProviderPayableLinePayment[]>> => {
	return ApiSaleOrderService.get(`/provider-payable-lines/${providerPayableLineId}/payments`);
};

// POST /provider-payable-lines/{id}/payments — REAL
export const createProviderPayableLinePayment = (
	providerPayableLineId: string,
	data: IProviderPayableLinePaymentCreateDTO,
): Promise<AxiosResponse<IProviderPayableLinePayment>> => {
	return ApiSaleOrderService.post(`/provider-payable-lines/${providerPayableLineId}/payments`, data);
};

// PUT /provider-payable-line-payments/{paymentId} — REAL
export const updateProviderPayableLinePayment = (
	paymentId: string,
	data: IProviderPayableLinePaymentUpdateDTO,
): Promise<AxiosResponse<IProviderPayableLinePayment>> => {
	return ApiSaleOrderService.put(`/provider-payable-line-payments/${paymentId}`, data);
};

export const bulkCreateProviderPayableLinePayments = (
	items: IBulkPaymentItem[],
): Promise<AxiosResponse<IBulkPaymentResponseItem[]>> => {
	return ApiSaleOrderService.post('/provider-payable-line-payments/bulk', items);
};

const normalizeProviderPayableLineFilters = (
	filters?: IProviderPayableLineListFilters | IProviderPayableLineScheduleFilters,
): Record<string, string | number | boolean | Array<string | number | boolean>> => {
	if (!filters) return {};

	return {
		...(filters.providerIds?.length ? { providerIds: filters.providerIds } : {}),
		...(filters.ticket ? { ticket: filters.ticket } : {}),
		...('saleOrderId' in filters && filters.saleOrderId ? { saleOrderId: filters.saleOrderId } : {}),
		...('sellerMarket' in filters && filters.sellerMarket ? { sellerMarket: filters.sellerMarket } : {}),
		...(filters.payableTypes?.length ? { payableTypes: filters.payableTypes } : {}),
		...(filters.paymentTypes?.length ? { paymentTypes: filters.paymentTypes } : {}),
		...(filters.paymentStatuses?.length ? { paymentStatuses: filters.paymentStatuses } : {}),
		...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
		...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
		...('scheduleDateFrom' in filters && filters.scheduleDateFrom
			? { scheduleDateFrom: filters.scheduleDateFrom }
			: {}),
		...('scheduleDateTo' in filters && filters.scheduleDateTo ? { scheduleDateTo: filters.scheduleDateTo } : {}),
	};
};

export const getProviderPayableLineList = (
	page = 1,
	size = 100,
	sortingFields: ISort[],
	filters?: IProviderPayableLineListFilters,
): Promise<AxiosResponse<IProviderPayableLineListResponse>> => {
	const sorts = formatSortingFields(sortingFields);

	return ApiSaleOrderService.get('/provider-payable-lines', {
		params: {
			page,
			size,
			...(sorts.length > 0 && { sort: sorts }),
			...normalizeProviderPayableLineFilters(filters),
		},
		paramsSerializer: (params) => {
			const searchParams = new URLSearchParams();

			Object.entries(params).forEach(([key, value]) => {
				if (value === undefined || value === null || value === '') return;

				if (Array.isArray(value)) {
					value.forEach((item) => {
						if (item !== undefined && item !== null && item !== '') {
							searchParams.append(key, String(item));
						}
					});
					return;
				}

				searchParams.append(key, String(value));
			});

			return searchParams.toString();
		},
	});
};

export const getProviderPayableLineSchedule = (
	filters: IProviderPayableLineScheduleFilters,
): Promise<AxiosResponse<IProviderPayableLineScheduleResponse>> => {
	return ApiSaleOrderService.get('/provider-payable-lines/schedule', {
		params: normalizeProviderPayableLineFilters(filters),
		paramsSerializer: (params) => {
			const searchParams = new URLSearchParams();

			Object.entries(params).forEach(([key, value]) => {
				if (value === undefined || value === null || value === '') return;

				if (Array.isArray(value)) {
					value.forEach((item) => {
						if (item !== undefined && item !== null && item !== '') {
							searchParams.append(key, String(item));
						}
					});
					return;
				}

				searchParams.append(key, String(value));
			});

			return searchParams.toString();
		},
	});
};

export const getAllProviderPayableLineListBySaleOrderId = (
	saleOrderId: string,
): Promise<AxiosResponse<IProviderPayableLineListItem[]>> => {
	return ApiSaleOrderService.get(`/provider-payable-lines/sale-orders/${saleOrderId}`);
};

export const getProviderPayableLinesPending = (
	ids: string[],
): Promise<AxiosResponse<IProviderPayableLinePending[]>> => {
	return ApiSaleOrderService.post<IProviderPayableLinePending[]>('/provider-payable-lines/pending-by-ids', {
		providerPayableLineIds: ids,
	});
};

const normalizeProviderPayableLinePaymentFilters = (
	filters?: IProviderPayableLinePaymentListFilters,
): Record<string, string | number | boolean | Array<string | number | boolean>> => {
	if (!filters) return {};

	return {
		...(filters.providerIds?.length ? { providerIds: filters.providerIds } : {}),
		...(filters.ticket ? { ticket: filters.ticket } : {}),
		...(filters.paymentTypes?.length ? { paymentTypes: filters.paymentTypes } : {}),
		...(filters.sellerMarkets?.length ? { sellerMarkets: filters.sellerMarkets } : {}),
		...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
		...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
		...(filters.createdFrom ? { createdFrom: filters.createdFrom } : {}),
		...(filters.createdTo ? { createdTo: filters.createdTo } : {}),
		...(filters.notes ? { notes: filters.notes } : {}),
		...(filters.observation ? { observation: filters.observation } : {}),
	};
};

export const getProviderPayableLinePaymentList = (
	page = 1,
	size = 100,
	sortingFields: ISort[],
	filters?: IProviderPayableLinePaymentListFilters,
): Promise<AxiosResponse<IProviderPayableLinePaymentListResponse>> => {
	const sorts = formatSortingFields(sortingFields);

	return ApiSaleOrderService.get('/provider-payable-line-payments', {
		params: {
			page,
			size,
			...(sorts.length > 0 && { sort: sorts }),
			...normalizeProviderPayableLinePaymentFilters(filters),
		},
		paramsSerializer: (params) => {
			const searchParams = new URLSearchParams();

			Object.entries(params).forEach(([key, value]) => {
				if (value === undefined || value === null || value === '') return;

				if (Array.isArray(value)) {
					value.forEach((item) => {
						if (item !== undefined && item !== null && item !== '') {
							searchParams.append(key, String(item));
						}
					});
					return;
				}

				searchParams.append(key, String(value));
			});

			return searchParams.toString();
		},
	});
};
// ─────────────────────────────────────────────────────────────────────────────
// FEATURE 2 — Cobros a Clientes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Receipts de una sale order específica (vista de detalle de orden).
 * Endpoint: GET /saleorders/{id}/receipts
 */
export const getReceiptsForOrder = (
	saleOrderId: string,
	options?: { agencyId?: number },
): Promise<AxiosResponse<IReceipt[]>> => {
	return ApiSaleOrderService.get(`/saleorders/${saleOrderId}/receipts`, {
		agencyId: options?.agencyId,
	});
};

/**
 * Emite un recibo: genera el PDF via API externa, guarda pdfUrl y marca issued=true.
 * Endpoint: PATCH /receipts/{id}/issue
 */
export const issueReceipt = (
	receiptId: string,
	passengerId: string,
): Promise<AxiosResponse<{ receiptNumber: string; pdfUrl: string }>> => {
	return ApiSaleOrderService.patch(`/receipts/${receiptId}/issue`, { passengerId });
};

/**
 * Marca/desmarca un receipt como conciliado.
 * Endpoint: PATCH /receipts/{id}/reconcile
 */
export const reconcileReceipt = (receiptId: string, reconciled: boolean): Promise<AxiosResponse<IReceipt>> => {
	return ApiSaleOrderService.patch(`/receipts/${receiptId}/reconcile`, { reconciled });
};

/**
 * Listado global de receipts con filtros, paginación y ordenamiento.
 * Endpoint: GET /receipts
 */

const normalizeReceiptFilters = (
	filters?: IReceiptFilters,
): Record<string, string | number | boolean | Array<string | number | boolean>> => {
	if (!filters) return {};

	return {
		...(filters.saleOrderId ? { saleOrderId: filters.saleOrderId } : {}),
		...(filters.receiptNumber ? { receiptNumber: filters.receiptNumber } : {}),
		...(filters.dealId !== undefined && filters.dealId !== null ? { dealId: filters.dealId } : {}),
		...(filters.reference ? { reference: filters.reference } : {}),
		...(filters.passengerName ? { passengerName: filters.passengerName } : {}),

		...(filters.agencyIds?.length ? { agencyIds: filters.agencyIds } : {}),
		...(filters.segmentTypes?.length ? { segmentTypes: filters.segmentTypes } : {}),
		...(filters.relationTypes?.length ? { relationTypes: filters.relationTypes } : {}),
		...(filters.platformTypes?.length ? { platformTypes: filters.platformTypes } : {}),
		...(filters.currencies?.length ? { currencies: filters.currencies } : {}),

		...(filters.bankName ? { bankName: filters.bankName } : {}),
		...(filters.cardStamps?.length ? { cardStamps: filters.cardStamps } : {}),

		...(filters.amountFrom !== undefined && filters.amountFrom !== null ? { amountFrom: filters.amountFrom } : {}),
		...(filters.amountTo !== undefined && filters.amountTo !== null ? { amountTo: filters.amountTo } : {}),

		...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
		...(filters.dateTo ? { dateTo: filters.dateTo } : {}),

		...(filters.reconciledFrom ? { reconciledFrom: filters.reconciledFrom } : {}),
		...(filters.reconciledTo ? { reconciledTo: filters.reconciledTo } : {}),

		...(filters.reconciled !== undefined && filters.reconciled !== null ? { reconciled: filters.reconciled } : {}),
		...(filters.issued !== undefined && filters.issued !== null ? { issued: filters.issued } : {}),
		...(filters.observations ? { observations: filters.observations } : {}),
	};
};

export const getReceiptList = (
	page: number,
	size: number,
	filters?: IReceiptFilters,
	sort?: ISort[],
): Promise<AxiosResponse<IReceiptListResponse>> => {
	return ApiSaleOrderService.get('/receipts', {
		params: {
			page,
			size,
			...(sort?.[0]?.field && { sortField: sort[0].field }),
			...(sort?.[0]?.order && { sortOrder: sort[0].order }),
			...normalizeReceiptFilters(filters),
		},
		paramsSerializer: (params) => {
			const searchParams = new URLSearchParams();

			Object.entries(params).forEach(([key, value]) => {
				if (value === undefined || value === null || value === '') return;

				if (Array.isArray(value)) {
					value.forEach((item) => {
						if (item !== undefined && item !== null && item !== '') {
							searchParams.append(key, String(item));
						}
					});
					return;
				}

				searchParams.append(key, String(value));
			});

			return searchParams.toString();
		},
	});
};
